import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { IPerson, selectPersonLoggedIn } from '../../store/slices/auth';
import { IDoctor, selectDoctors } from '../../store/slices/doctors';

import moment from 'moment';
import styles from './Chat.module.scss';
import { useTranslation } from '../../hooks/useTranslations';
import { updateChatDoctorName } from '../../store/slices/chat';

interface Props {
  currentConversation: any;
  addChatParticipant: any;
  hideAssignBtn: boolean;
  closeConversation: any;
}

export default function ChatHeader({
  currentConversation,
  addChatParticipant,
  hideAssignBtn,
  closeConversation,
}: Props) {
  const { person_external_id } = useAppSelector<IPerson>(selectPersonLoggedIn);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const { translations } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const hideActions = location.pathname.includes('/web/video-call');

  const participant = currentConversation?.participants?.find((el: any) => el.assigned_as === 'D');
  const customer = currentConversation?.participants?.find((el: any) => el.assigned_as === 'C');

  const ref = useRef<HTMLInputElement | undefined>();
  const [openDropDown, setOpenDropDown] = useState<boolean>(false);
  const [selectedItem, setSelecteditem] = useState<string>('');
  const doctorsIds = doctors.map((el) => el.person_external_id);
  const doctorIsAssigned = doctorsIds.includes(selectedItem);
  const dispatch = useAppDispatch();

  const assignDoctor = (person_external_id: any) => {
    const doctor = doctors.find((el) => el.person_external_id === person_external_id);
    addChatParticipant({
      conversationId: currentConversation.conversation_id,
      participant: {
        participantPersonId: parseInt(person_external_id),
        participantName: doctor?.person_firstname,
        participantLastName: doctor?.person_lastname,
      },
    });
    setSelecteditem(person_external_id);
    setOpenDropDown(false);
    dispatch(
      updateChatDoctorName({
        doctorId: person_external_id,
        personName: doctor?.person_firstname || '',
        personLastname: doctor?.person_lastname || '',
      })
    );
  };

  const closeChatStatus = () => {
    closeConversation({ conversationId: currentConversation.conversation_id });
  };

  const getDoctorName = () => {
    // eslint-disable-next-line
    const doctor = doctors?.find((item) => item.person_external_id == selectedItem);
    return `${doctor?.person_firstname} ${doctor?.person_lastname}`;
  };

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      // If the menu is open and the clicked target is not within the menu, then close the menu
      if (openDropDown && ref.current && !ref.current.contains(e.target)) {
        setOpenDropDown(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [openDropDown]);

  const handleVideoCall = () => {
    navigate(`/web/video-call?id=${currentConversation.conversation_id}&action=initiate-call`);
  };

  // const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  // const isMobileClinic = accountInfo.consultation_with_extra_steps;

  return (
    <div className={`${styles.chatHeader}`}>
      <div>
        {customer && (
          <p className={styles.title}>{`${customer?.person_name} ${customer?.person_lastname}`}</p>
        )}
        <p className={styles.subTitle}>
          {`${currentConversation.conversation_title} ${moment(
            currentConversation.conversation_date
          ).format(' DD.MM.YYYY - HH:mm')}` || ''}
        </p>
      </div>
      {hideActions ? null : (
        <div className={styles.actionsWrap}>
          {!hideAssignBtn && participant?.person_id != person_external_id ? (
            <>
              {/* <div className={styles.devider} /> */}
              <div className={styles.dropdown} ref={ref as React.RefObject<HTMLDivElement>}>
                {doctorIsAssigned ? (
                  <button
                    className={styles.assignBtn}
                    disabled={currentConversation.conversation_status === 'C'}
                    onClick={() => setOpenDropDown(!openDropDown)}
                  >
                    {getDoctorName()}
                    {currentConversation.conversation_status !== 'C' && (
                      <i className={styles['arrow-down']}></i>
                    )}
                  </button>
                ) : (
                  <button
                    className={styles.assignBtn}
                    disabled={currentConversation.conversation_status === 'C'}
                    onClick={() => setOpenDropDown(!openDropDown)}
                  >
                    {translations('assign')}
                    {currentConversation.conversation_status !== 'C' && (
                      <i className={styles['arrow-down']}></i>
                    )}
                  </button>
                )}

                <ul
                  className={openDropDown ? `${styles.list} ${styles._visible}` : `${styles.list}`}
                >
                  {doctors?.map((item) => (
                    <li
                      key={item.person_id}
                      // eslint-disable-next-line
                      className={
                        selectedItem == item.person_external_id
                          ? `${styles.item} ${styles._active}`
                          : styles.item
                      }
                      onClick={() => assignDoctor(item.person_external_id)}
                    >
                      {`${item.person_firstname} ${item.person_lastname} (${item.designation})`}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}
          <button
            className={styles.closeBtn}
            onClick={closeChatStatus}
            disabled={currentConversation.conversation_status === 'C'}
          >
            {translations('close')}
          </button>
          {currentConversation.conversation_status !== 'C' ? (
            <button className={styles.videoBtn} onClick={handleVideoCall}>
              <i className={styles.videoCallIcon}></i>
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
